// src/utils/profile.js
// Supabase-backed profile management using the existing schema.
//
// Important:
// - Normalized voice profiles are stored inside public.brand_voices.voice_text as JSON.
// - We are intentionally NOT requiring a DB migration for this pass.
// - brand_settings still stores active_voice_id + snark_level for back-compat.
// - The profile format lives in voiceProfile.js; reading settings and saving the
//   active voice live in voiceSettingsStore.js (both testable in plain Node).

import { supabase } from "../lib/supabaseClient";
import { normalizeVoiceProfile, serializeVoiceProfile } from "./voiceProfile.js";
import { createVoiceSettingsStore } from "./voiceSettingsStore.js";

export { normalizeVoiceProfile, serializeVoiceProfile };

const voiceSettings = createVoiceSettingsStore({ supabase });

/**
 * Fetch the user's brand profile from Supabase (auth.uid scoped).
 * Returns a shape compatible with the current UI while adding normalized voice profiles.
 */
export function getProfile() {
  return voiceSettings.getProfile();
}

/**
 * Make one of the user's own voices active (auth.uid scoped). Writes only
 * brand_settings.active_voice_id and never rewrites a voice, so approved voices
 * stay approved.
 */
export function saveActiveVoice(activeVoiceId) {
  return voiceSettings.saveActiveVoice(activeVoiceId);
}

/**
 * Save the user's profile to Supabase (auth.uid scoped).
 * - Upserts voices
 * - Deletes voices removed locally
 * - Upserts brand_settings (snark + active_voice_id)
 *
 * NOTE: email argument is ignored (legacy signature).
 */
export async function saveProfile(_emailIgnored, profile) {
  const { data: auth, error: authErr } = await supabase.auth.getUser();
  if (authErr) throw authErr;

  const userId = auth?.user?.id;
  if (!userId) throw new Error("Not signed in");

  const voices = Array.isArray(profile?.voices) ? profile.voices : [];
  if (voices.length > 5) throw new Error("Max 5 brand voices allowed.");

  const fallbackActiveId = voices[0]?.id || null;
  const desiredActiveId = profile?.active_voice_id || fallbackActiveId;
  const activeId = voices.some((v) => v.id === desiredActiveId)
    ? desiredActiveId
    : fallbackActiveId;

  const rows = voices.map((v) => {
    const normalized = normalizeVoiceProfile(
      v.profile || v.text || "",
      profile?.snark_level ?? 0.3
    );

    if (!normalized.voice_name && v.name) {
      normalized.voice_name = v.name;
    }

    return {
      id: v.id,
      user_id: userId,
      name: v.name || normalized.voice_name || "Voice",
      voice_text: serializeVoiceProfile(normalized),
    };
  });

  const incomingIds = new Set(rows.map((r) => r.id));

  const { data: existing, error: exErr } = await supabase
    .from("brand_voices")
    .select("id")
    .eq("user_id", userId);

  if (exErr) throw exErr;

  const toDelete = (existing || [])
    .map((x) => x.id)
    .filter((id) => !incomingIds.has(id));

  // Delete removed voices before upserting the new/edited set. Otherwise, replacing
  // a voice while already at the 5-voice cap briefly asks for 6 rows to exist at
  // once (old + new), which trips the DB trigger and aborts the save entirely —
  // leaving the old voice permanently stuck since the delete never gets to run.
  if (toDelete.length) {
    const { error: delErr } = await supabase
      .from("brand_voices")
      .delete()
      .eq("user_id", userId)
      .in("id", toDelete);

    if (delErr) throw delErr;
  }

  const { error: voicesErr } = await supabase
    .from("brand_voices")
    .upsert(rows, { onConflict: "id" });

  if (voicesErr) throw voicesErr;

  const activeVoice = rows.find((v) => v.id === activeId) || rows[0] || null;
  const activeProfile = activeVoice
    ? normalizeVoiceProfile(activeVoice.voice_text, profile?.snark_level ?? 0.3)
    : null;

  const snark =
    typeof activeProfile?.snark_level === "number"
      ? activeProfile.snark_level
      : typeof profile?.snark_level === "number"
      ? profile.snark_level
      : 0.3;

  const { error: settingsErr } = await supabase.from("brand_settings").upsert({
    user_id: userId,
    snark_level: snark,
    active_voice_id: activeId,
  });

  if (settingsErr) throw settingsErr;

  return true;
}

/**
 * Delete the user's settings + voices (auth.uid scoped).
 * NOTE: email argument is ignored (legacy signature).
 */
export async function deleteProfile(_emailIgnored) {
  const { data: auth, error: authErr } = await supabase.auth.getUser();
  if (authErr) throw authErr;

  const userId = auth?.user?.id;
  if (!userId) return;

  const [{ error: sErr }, { error: vErr }] = await Promise.all([
    supabase.from("brand_settings").delete().eq("user_id", userId),
    supabase.from("brand_voices").delete().eq("user_id", userId),
  ]);

  if (sErr) throw sErr;
  if (vErr) throw vErr;
}